/**
 * Pick-step + nested SIEM/audit viewers while the drop timeline modal is open.
 */

import { useCallback, useEffect, useState } from 'react';

import { useAuditLog, useSiemEvent, type DropTimelineItem } from '@/hooks';
import AuditLogDetailsModal from '@/features/admin/audit/components/AuditLogDetailsModal';
import { SiemEventDetailsModal } from '@/features/siem/components/SiemEventDetailsModal';
import { useAuth } from '@/providers/AuthProvider';
import type { DropTimelinePickTarget } from '@/features/siem/utils/drop-timeline';

const NESTED_OVERLAY_Z = 'z-[70]';

export function useDropTimelineModalChrome(modalOpen: boolean) {
  const [picked, setPicked] = useState<DropTimelinePickTarget | null>(null);
  const [siemId, setSiemId] = useState('');
  const [auditId, setAuditId] = useState('');

  useEffect(() => {
    if (!modalOpen) {
      setPicked(null);
      setSiemId('');
      setAuditId('');
    }
  }, [modalOpen]);

  const openItem = useCallback((item: DropTimelineItem) => {
    setSiemId('');
    setAuditId('');
    if (item.source === 'siem') setSiemId(item.id);
    else setAuditId(item.id);
  }, []);

  const openAuditFromSiem = useCallback((logId: string) => {
    setSiemId('');
    setAuditId(logId);
  }, []);

  return {
    picked,
    setPicked,
    siemId,
    auditId,
    openItem,
    closeSiem: () => setSiemId(''),
    closeAudit: () => setAuditId(''),
    openAuditFromSiem,
  };
}

export function DropTimelineNestedDetails(props: {
  siemId: string;
  auditId: string;
  onCloseSiem: () => void;
  onCloseAudit: () => void;
  onOpenAuditFromSiem: (auditLogId: string) => void;
}) {
  const { hasPermission } = useAuth();
  const canViewAudit = hasPermission('audit.view');
  const { data: siemEvent } = useSiemEvent(props.siemId);
  const { data: auditLog } = useAuditLog(props.auditId);

  return (
    <>
      <SiemEventDetailsModal
        open={props.siemId !== ''}
        overlayZClass={NESTED_OVERLAY_Z}
        onOpenChange={(o) => {
          if (!o) props.onCloseSiem();
        }}
        event={siemEvent ?? null}
        onOpenAuditLog={
          canViewAudit
            ? (auditLogId) => {
                props.onCloseSiem();
                props.onOpenAuditFromSiem(auditLogId);
              }
            : undefined
        }
      />
      <AuditLogDetailsModal
        open={props.auditId !== ''}
        overlayZClass={NESTED_OVERLAY_Z}
        onOpenChange={(o) => {
          if (!o) props.onCloseAudit();
        }}
        log={auditLog ?? null}
      />
    </>
  );
}
